/**
 * Automotive-Safe SEO & Catalog Slugify Utilities
 * Parts4Drive Platform — ECOWAS Programmatic Marketplace
 */

/**
 * Automotive-Safe Slugify Function
 *
 * Processes specialized automotive terms cleanly:
 * - Converts text to strictly lowercase and trims whitespace
 * - Substitutes slashes (/ and \), spaces, and underscores (_) with a single clean hyphen (-)
 * - Explicitly preserves numerical decimal points (.) so engine displacements (e.g., "2.5L", "1.8L") don't break
 * - Drops all other special characters
 * - Collapses consecutive hyphens and trims leading/trailing hyphens
 *
 * @example
 * automotiveSlugify("2.5L 4-Cylinder") => "2.5l-4-cylinder"
 * automotiveSlugify("16V/VVT-i") => "16v-vvt-i"
 * automotiveSlugify("Brake Rotors & Discs") => "brake-rotors-discs"
 */
export function automotiveSlugify(text: string): string {
  if (!text) return '';
  return text
    .trim()
    .toLowerCase()
    // Replace slashes, backslashes, underscores, and whitespace sequences with a single hyphen
    .replace(/[/\\_\s]+/g, '-')
    // Drop all characters except lowercase alphanumeric, hyphens, and decimal dots
    .replace(/[^a-z0-9.-]/g, '')
    // Clean up multiple consecutive dots
    .replace(/\.{2,}/g, '.')
    // Collapse multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Trim leading/trailing hyphens or dots
    .replace(/^[-.]+|[-.]+$/g, '');
}

/**
 * Known automotive abbreviations that require uppercase preservation
 */
const KNOWN_ACRONYMS: Record<string, string> = {
  oem: 'OEM',
  abs: 'ABS',
  amg: 'AMG',
  led: 'LED',
  hid: 'HID',
  ecu: 'ECU',
  tcm: 'TCM',
  tps: 'TPS',
  maf: 'MAF',
  egr: 'EGR',
  cv: 'CV',
  awd: 'AWD',
  fwd: 'FWD',
  rwd: 'RWD',
  '4wd': '4WD',
  v6: 'V6',
  v8: 'V8',
  v10: 'V10',
  v12: 'V12',
  i4: 'I4',
  i6: 'I6',
  ev: 'EV',
  p4d: 'P4D',
  gh: 'GH',
  ng: 'NG',
  le: 'LE',
  se: 'SE',
  xle: 'XLE',
  xse: 'XSE',
  sr5: 'SR5',
  trd: 'TRD',
  gti: 'GTI',
  ex: 'EX',
  lx: 'LX',
  dx: 'DX',
};

const COMPOUND_TERMS: Record<string, string> = {
  'vvt-i': 'VVT-i',
  'd-4s': 'D-4S',
  'cr-v': 'CR-V',
  'hr-v': 'HR-V',
  'c-class': 'C-Class',
  'e-class': 'E-Class',
  's-class': 'S-Class',
  'a-class': 'A-Class',
  'm-sport': 'M-Sport',
  'amg-line': 'AMG-Line',
  'f-150': 'F-150',
};

/**
 * Converts slugified URL parameters back to human-readable presentation text
 * - Replaces hyphens with spaces
 * - Preserves decimal engine displacements (e.g., "2.5l" -> "2.5L")
 * - Capitalizes standard words and respects known automotive acronyms
 *
 * @example
 * unslugifyAutomotive("toyota") => "Toyota"
 * unslugifyAutomotive("2.5l-4-cylinder") => "2.5L 4 Cylinder"
 * unslugifyAutomotive("brake-rotors-discs") => "Brake Rotors Discs"
 */
export function unslugifyAutomotive(slug: string): string {
  if (!slug) return '';
  let decoded = decodeURIComponent(slug).toLowerCase();

  // Substitute known compound terms with distinct placeholders
  const placeholderMap: Record<string, string> = {};
  let pIdx = 0;
  for (const [pattern, replacement] of Object.entries(COMPOUND_TERMS)) {
    if (decoded.includes(pattern)) {
      const token = `__P4D_TOKEN_${pIdx++}__`;
      placeholderMap[token] = replacement;
      decoded = decoded.split(pattern).join(token);
    }
  }

  // Split into hyphenated words
  const words = decoded.split('-');

  const formatted = words
    .map((word) => {
      if (!word) return '';

      // Check placeholder tokens
      for (const [token, replacement] of Object.entries(placeholderMap)) {
        if (word.includes(token)) {
          return word.replace(token, replacement);
        }
      }

      const lower = word.toLowerCase();

      // Check known acronyms
      if (KNOWN_ACRONYMS[lower]) {
        return KNOWN_ACRONYMS[lower];
      }

      // Check decimal displacement patterns like "2.5l", "1.8l", "3.0l"
      const engineDisplacementMatch = lower.match(/^(\d+\.\d+)l$/);
      if (engineDisplacementMatch) {
        return `${engineDisplacementMatch[1]}L`;
      }

      // Check cylinder patterns like "4cyl", "6cyl"
      const cylinderMatch = lower.match(/^(\d+)cyl$/);
      if (cylinderMatch) {
        return `${cylinderMatch[1]}Cyl`;
      }

      // Check valve patterns like "16v", "24v"
      const valveMatch = lower.match(/^(\d+)v$/);
      if (valveMatch) {
        return `${valveMatch[1]}V`;
      }

      // Standard capitalization
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  // Final replacement of any remaining placeholders
  let result = formatted;
  for (const [token, replacement] of Object.entries(placeholderMap)) {
    result = result.split(token).join(replacement);
  }

  return result;
}
